import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdAdd, MdWarningAmber, MdShare, MdDelete } from 'react-icons/md';
import { toast } from 'react-toastify';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import {
    fetchManufacturerComplianceDocs,
    createManufacturerComplianceDoc,
    updateManufacturerComplianceDoc,
    shareManufacturerComplianceDoc,
    deleteManufacturerComplianceDoc,
} from '../../../../services/Http/ManufacturerApi';

const docTypes = ['dpp', 'regulatory', 'certificate'];

const ManufacturerCompliance = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState({
        doc_type: 'dpp',
        title: '',
        description: '',
        file_url: '',
        expires_at: '',
    });
    const [saving, setSaving] = useState(false);
    const [sharingId, setSharingId] = useState(null);
    const [shareTargets, setShareTargets] = useState('');

    const loadDocuments = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const response = await fetchManufacturerComplianceDocs();
            setDocuments(response.data || []);
        } catch (err) {
            console.error(err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDocuments();
    }, [loadDocuments]);

    const upcomingExpirations = useMemo(() => {
        return (documents || []).filter((doc) => doc.days_to_expire !== null && doc.days_to_expire <= 30);
    }, [documents]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            if (form.id) {
                await updateManufacturerComplianceDoc(form.id, form);
                toast.success(t('ManufacturerCompliance.toast.updated'));
            } else {
                await createManufacturerComplianceDoc(form);
                toast.success(t('ManufacturerCompliance.toast.created'));
            }
            setForm({
                doc_type: 'dpp',
                title: '',
                description: '',
                file_url: '',
                expires_at: '',
            });
            setFormOpen(false);
            await loadDocuments();
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerCompliance.toast.error'));
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (doc) => {
        setForm({
            id: doc.id,
            doc_type: doc.doc_type,
            title: doc.title,
            description: doc.description,
            file_url: doc.file_url,
            expires_at: doc.expires_at || '',
        });
        setFormOpen(true);
    };

    const handleShare = async (doc) => {
        try {
            const targets = shareTargets.split(',').map((value) => value.trim()).filter(Boolean);
            await shareManufacturerComplianceDoc(doc.id, { shared_with: targets });
            toast.success(t('ManufacturerCompliance.toast.shared'));
            setSharingId(null);
            setShareTargets('');
            await loadDocuments();
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerCompliance.toast.error'));
        }
    };

    const handleDelete = async (doc) => {
        if (!window.confirm(t('ManufacturerCompliance.actions.confirmDelete'))) {
            return;
        }
        try {
            await deleteManufacturerComplianceDoc(doc.id);
            toast.success(t('ManufacturerCompliance.toast.deleted'));
            await loadDocuments();
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerCompliance.toast.error'));
        }
    };

    if (loading) {
        return <LoadingMessage>{t('ManufacturerCompliance.loading')}</LoadingMessage>;
    }

    if (error) {
        return <ErrorMessage>{t('ManufacturerCompliance.error')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <TitleGroup>
                    <PageTitle>{t('ManufacturerCompliance.title')}</PageTitle>
                    <Subtitle>{t('ManufacturerCompliance.subtitle')}</Subtitle>
                </TitleGroup>
                <PrimaryButton type="button" onClick={() => setFormOpen(true)}>
                    <MdAdd /> {t('ManufacturerCompliance.actions.add')}
                </PrimaryButton>
            </PageHeader>
            <PageContent>
                {upcomingExpirations.length > 0 && (
                    <AlertBox>
                        <MdWarningAmber />
                        <span>
                            {t('ManufacturerCompliance.alerts.expiring', {
                                count: upcomingExpirations.length,
                            })}
                        </span>
                    </AlertBox>
                )}

                {formOpen && (
                    <Form onSubmit={handleSubmit}>
                        <FormRow>
                            <label>{t('ManufacturerCompliance.form.type')}</label>
                            <select
                                value={form.doc_type}
                                onChange={(event) => setForm((prev) => ({ ...prev, doc_type: event.target.value }))}
                            >
                                {docTypes.map((type) => (
                                    <option key={type} value={type}>
                                        {t(`ManufacturerCompliance.types.${type}`)}
                                    </option>
                                ))}
                            </select>
                        </FormRow>
                        <FormRow>
                            <label>{t('ManufacturerCompliance.form.title')}</label>
                            <input
                                type="text"
                                value={form.title}
                                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                                required
                            />
                        </FormRow>
                        <FormRow>
                            <label>{t('ManufacturerCompliance.form.description')}</label>
                            <textarea
                                value={form.description}
                                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                            />
                        </FormRow>
                        <FormRow>
                            <label>{t('ManufacturerCompliance.form.file')}</label>
                            <input
                                type="url"
                                value={form.file_url}
                                onChange={(event) => setForm((prev) => ({ ...prev, file_url: event.target.value }))}
                            />
                        </FormRow>
                        <FormRow>
                            <label>{t('ManufacturerCompliance.form.expires')}</label>
                            <input
                                type="date"
                                value={form.expires_at}
                                onChange={(event) => setForm((prev) => ({ ...prev, expires_at: event.target.value }))}
                            />
                        </FormRow>
                        <Actions>
                            <SecondaryButton type="button" onClick={() => setFormOpen(false)}>
                                {t('ManufacturerCompliance.actions.cancel')}
                            </SecondaryButton>
                            <PrimaryButton type="submit" disabled={saving}>
                                {saving ? t('ManufacturerCompliance.actions.saving') : t('ManufacturerCompliance.actions.save')}
                            </PrimaryButton>
                        </Actions>
                    </Form>
                )}

                <Table>
                    <thead>
                        <tr>
                            <th>{t('ManufacturerCompliance.table.doc')}</th>
                            <th>{t('ManufacturerCompliance.table.type')}</th>
                            <th>{t('ManufacturerCompliance.table.expires')}</th>
                            <th>{t('ManufacturerCompliance.table.status')}</th>
                            <th>{t('ManufacturerCompliance.table.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {documents.map((doc) => (
                            <tr key={doc.id} className={doc.days_to_expire !== null && doc.days_to_expire <= 30 ? 'expiring' : ''}>
                                <td>
                                    <strong>{doc.title}</strong>
                                    <p>{doc.description}</p>
                                </td>
                                <td>{t(`ManufacturerCompliance.types.${doc.doc_type}`)}</td>
                                <td>{doc.expires_at || '—'}</td>
                                <td>{t(`ManufacturerCompliance.status.${doc.status}`)}</td>
                                <td>
                                    <RowActions>
                                        <button type="button" onClick={() => handleEdit(doc)}>
                                            {t('ManufacturerCompliance.actions.edit')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSharingId(doc.id);
                                                setShareTargets(doc.shared_with?.join(', ') || '');
                                            }}
                                        >
                                            <MdShare />
                                        </button>
                                        <button type="button" onClick={() => handleDelete(doc)}>
                                            <MdDelete />
                                        </button>
                                    </RowActions>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>

                {sharingId && (
                    <ShareModal>
                        <div>
                            <h3>{t('ManufacturerCompliance.share.title')}</h3>
                            <p>{t('ManufacturerCompliance.share.description')}</p>
                            <input
                                type="text"
                                value={shareTargets}
                                onChange={(event) => setShareTargets(event.target.value)}
                                placeholder={t('ManufacturerCompliance.share.placeholder')}
                            />
                            <Actions>
                                <SecondaryButton type="button" onClick={() => setSharingId(null)}>
                                    {t('ManufacturerCompliance.actions.cancel')}
                                </SecondaryButton>
                                <PrimaryButton type="button" onClick={() => handleShare({ id: sharingId })}>
                                    {t('ManufacturerCompliance.actions.share')}
                                </PrimaryButton>
                            </Actions>
                        </div>
                    </ShareModal>
                )}
            </PageContent>
        </PageContainer>
    );
};

export default ManufacturerCompliance;

const TitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    border: none;
    background: rgba(130, 22, 146, 1);
    color: #fff;
    border-radius: 8px;
    padding: 0.5rem 0.875rem;
    cursor: pointer;
`;

const SecondaryButton = styled.button`
    border: 1px solid rgba(130, 22, 146, 0.3);
    background: transparent;
    color: rgba(130, 22, 146, 1);
    border-radius: 8px;
    padding: 0.5rem 0.875rem;
    cursor: pointer;
`;

const Tabs = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const TabButton = styled.button`
    border: none;
    background: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 0.15)' : 'transparent')};
    color: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 1)' : '#444')};
    border-radius: 999px;
    padding: 0.375rem 0.875rem;
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    cursor: pointer;
`;

const AlertBox = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.875rem;
    background: rgba(255, 152, 0, 0.1);
    border: 1px solid rgba(255, 152, 0, 0.3);
    border-radius: 8px;
    margin-bottom: 1rem;
`;

const Form = styled.form`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1rem;
    margin-bottom: 1.125rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const FormRow = styled.label`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;

    input,
    select,
    textarea {
        border-radius: 8px;
        border: 1px solid rgba(130, 22, 146, 0.2);
        padding: 0.5rem;
    }
`;

const Actions = styled.div`
    display: flex;
    gap: 0.625rem;
    justify-content: flex-end;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        border-bottom: 1px solid rgba(130, 22, 146, 0.1);
        padding: 0.625rem;
        text-align: left;
    }

    tr.expiring {
        background: rgba(255, 152, 0, 0.05);
    }
`;

const RowActions = styled.div`
    display: flex;
    gap: 0.5rem;

    button {
        border: none;
        background: rgba(130, 22, 146, 0.1);
        border-radius: 6px;
        padding: 0.375rem;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
    }
`;

const ShareModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;

    > div {
        background: #fff;
        border-radius: 12px;
        padding: 1.25rem;
        width: min(400px, 90%);
        display: flex;
        flex-direction: column;
        gap: 0.75rem;

        input {
            border-radius: 8px;
            border: 1px solid rgba(130, 22, 146, 0.2);
            padding: 0.5rem;
        }
    }
`;
